import {
  AlertColor,
  Box,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
} from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import DrawerMenu from 'components/DrawerMenu';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import Delete from 'screens/Authenticated/CommonPage/Delete';
import { createDropdownData, fetchIdsFromSelectedDropdownValues } from 'utils/validators/HelperFunctions';
import strings from '../../../../../common/Translation/Translate';
import Form from '../../../../../components/Form';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import AddEdit from '../../../CommonPage/AddEdit';
import { AddEditCompanyMasterForm } from './AddEditCompanyMasterForm';
import BranchInfo from '../BranchInfo';
import ListPageImportDrawerComponent from '../../../CommonPage/ListPageImportDrawerComponent';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CorporateRateInfo from '../BranchInfo/CorporateRateInfo';
import CompanyLedgerInfo from '../CompanyLedgerInfo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { fetchFromStorage } from 'utils/storage';

const AddEditCompanyMaster: FC = () => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams() as any;
  const [loading, setLoading] = useState(false);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [openBranchInfoDrawer, setOpenBranchInfoDrawer] = useState<boolean>(false);
  const [corporateRates, setCorporateRates] = useState<any>([]);
  const [openImportDrawer, setOpenImportDrawer] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [openCorporateRatesDrawer, setOpenCorporateRatesDrawer] = useState<boolean>(false);
  const [industryTypeList, setIndustryTypeList] = useState<any>([]);
  const [accountTypes, setAccountTypes] = useState<any>([]);
  const [companyCategoryList, setCompanyCategoryList] = useState<any>([]);
  const [countryList, setCountryList] = useState<any>([]);
  const [cityList, setCityList] = useState<any>([]);
  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [branches, setBranches] = useState<any>([]);
  const [branchId, setBranchId] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<{ [key: string]: boolean }>({
    panel1: true,
    panel2: true,
    panel3: true,
    panel4: false,
  });

  const isSalesManagementEnabled = fetchFromStorage('isSalesManagementEnabled') === 'true';

  const [statusList, setStatusList] = useState<any>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const getMasterData = useCallback(async () => {
    const { data } = await axiosInstance.get(URLS.leadDropdown);
    setAssigneeList(createDropdownData(data?.assignees, ['id', 'name']));
    setCompanyCategoryList(createDropdownData(data?.companyCategories, ['id', 'name']));
    setCountryList(createDropdownData(data?.countries, ['id', 'name']));
    setStatusList(createDropdownData(data?.statuses, ['id', 'name']));
    setIndustryTypeList(createDropdownData(data?.industryTypes, ['id', 'name']));
    setAccountTypes(createDropdownData(data?.accountTypes, ['id', 'name']));
  }, []);

  useEffect(() => {
    getMasterData();
  }, [getMasterData]);

  const handleCatch = (error: any) => {
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.error_description ?? error.response?.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const getInitialResponse = useCallback(async () => {
    try {
      setLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getCompanyById(id));
      if (status === 200) {
        if (data?.hqCountry?.value) {
          const res = await axiosInstance.get(URLS.leadCitiesDropdown(data?.hqCountry?.value));
          setCityList(createDropdownData(res?.data, ['id', 'name']));
        }
        setPageResponse({
          ...data,
          companyCategory: data?.companyCategory.value,
          hqCountry: data?.hqCountry.value,
          hqCity: data?.hqCity.value,
          primaryManagers: data.primaryManagers,
          industryType: data?.industryType.value,
          accountType: data?.accountType.value,
        });
        setCorporateRates(data?.corporateRates || []);
        setBranches(data?.branches);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
      } else {
        const searchParams = new URLSearchParams(location.search);
        const hqEmail = searchParams.get('email') || '';
        const companyName = searchParams.get('companyName') || '';
        const hqPhoneNo = searchParams.get('phone') ? JSON.parse(searchParams.get('phone') || '') : '';
        if (hqEmail || companyName || hqPhoneNo) {
          setPageResponse({
            hqEmail,
            companyName,
            hqPhoneNo,
          });
        }
      }
    })();
  }, [getInitialResponse, id, location.search]);

  const handleCustomResponse = (custom: any) => {
    if (custom) {
      return {
        ...custom,
        hqPhoneNo: custom?.hqPhoneNo ? custom?.hqPhoneNo : null,
        primaryManagers: fetchIdsFromSelectedDropdownValues(custom.primaryManagers),
      };
    }
  };

  const pageConfig = {
    title: id ? strings.lead_management_edit : strings.lead_management_add,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id ? URLS.getCompanyById(id) : URLS.addCompany,
    deleteApi: URLS.deleteAgent,
    initialApi: URLS.getCompanyById(id),
    formModel: AddEditCompanyMasterForm(
      strings,
      companyCategoryList,
      countryList,
      cityList,
      assigneeList,
      statusList,
      industryTypeList,
      accountTypes,
    ),
    navigateToPage: id ? routes.companyMasters : null,
    isCustomResponse: true,
    hideForm: !!id, // Hide form in AddEdit when editing (id exists), show in accordion instead
  };

  const customCallbackResponse = (callbackValue: any) => {
    if (!id) {
      navigate(`${routes.editCompanyMasters}/${callbackValue.id}`);
    }
  };

  const validateCompanyName = async (name: string) => {
    if (name === pageResponse.companyName) return;
    if (!name || name.length < 3) return;

    try {
      const { status } = await axiosInstance.get(URLS.validateCompany(name));
      if (status === 200) {
        setMessage({ display: false, severity: null, message: '' });
      }
    } catch (error: any) {
      handleCatch(error);
    }
  };

  const debouncedValidate = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (name: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        validateCompanyName(name);
      }, 1000);
    };
  }, []);

  const onChange = async (field: string, value: any) => {
    if (field === 'companyName') {
      setPageResponse({ ...pageResponse, [field]: value });
      debouncedValidate(value);
    } else if (field === 'hqCountry') {
      try {
        formRef.current?.handleChange('', 'hqCity', { error: true, message: strings.requiredText });
        setPageResponse({ ...pageResponse, hqCountry: value, city: '' });
        setLoading(true);
        const res = await axiosInstance.get(URLS.leadCitiesDropdown(value));
        setCityList(createDropdownData(res?.data, ['id', 'name']));
      } catch (e) {
        handleCatch(e);
      } finally {
        setLoading(false);
      }
    } else {
      setPageResponse({ ...pageResponse, [field]: value });
    }
  };

  const editRow = (idx: number) => {
    const editValue = branches.find((item: any, i: any) => i === idx);

    setOpenBranchInfoDrawer(true);
    setBranchId(editValue.id);
  };

  const deleteBranch = async (idx: number) => {
    const editValue = branches.find((item: any, i: any) => i === idx);

    setShowDeleteModal(true);
    setBranchId(editValue.id);
  };

  const closeModal = () => {
    setBranchId(null);
    setShowDeleteModal(false);
  };

  const deleteModal = useMemo(() => {
    return (
      <Delete
        updateList={() => getInitialResponse()}
        closeModal={closeModal}
        openModal={showDeleteModal}
        deleteUrl={URLS.getBranchById(id, branchId)}
      />
    );
  }, [branchId, getInitialResponse, id, showDeleteModal]);

  const companyBranchesTable = () => {
    const columns: GridColDef[] = [
      { field: 'branchName', headerName: strings.sales_company_branch_name, flex: 1, minWidth: 150 },
      {
        field: 'contactsCount',
        headerName: 'Contacts',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params: any) => {
          return params.row.contactsCount ?? 0;
        },
      },
      {
        field: 'cityPotentialsCount',
        headerName: 'City Potentials',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params: any) => {
          return params.row.cityPotentialsCount ?? 0;
        },
      },
      {
        field: 'Action',
        headerName: strings.customermgmt_column_action,
        sortable: false,
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (ValueFormatterParams: any) => {
          return (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                color="info"
                aria-label="edit"
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  editRow(ValueFormatterParams.row.id);
                }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                aria-label="delete"
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  deleteBranch(ValueFormatterParams.row.id);
                }}>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        },
      },
    ];

    return branches?.length > 0 ? (
      <div style={{ width: '100%' }}>
        <div style={{ height: 300, width: '100%' }}>
          <DataGrid
            sx={{
              overflow: 'auto',
              '.MuiDataGrid-row': {
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: `${theme.palette.primary.main}09}`,
                },
              },
              '.MuiDataGrid-columnHeaders': {
                backgroundColor: `${theme.palette.primary.main}35}`,
              },
            }}
            rowHeight={60}
            rows={branches.map((item: any, idx: any) => ({
              ...item,
              id: idx,
            }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
          />
        </div>
      </div>
    ) : null;
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(prev => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  const handleAddBranch = () => {
    setBranchId(null);
    setOpenBranchInfoDrawer(true);
  };

  const handleAddCorporateRate = () => {
    setOpenCorporateRatesDrawer(true);
  };

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}

      <AddEdit
        handlePropsChange={onChange}
        handleCustomResponse={handleCustomResponse}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        customCallbackResponse={customCallbackResponse}
        externalFormRef={id ? formRef : undefined}>
        {id && (
          <>
            {/* Accordion 1: Edit Company Master Form */}
            <Accordion
              expanded={expandedAccordion.panel1}
              onChange={handleAccordionChange('panel1')}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': {
                  display: 'none',
                },
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {strings.lead_management_edit || 'Edit Company Master'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Company Details, Headquarter Details
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ my: 1 }}>
                  <Form
                    isFormUpdated={id}
                    dropdownData={{}}
                    hasError={false}
                    ref={formRef as RefObject<Form>}
                    model={pageConfig.formModel}
                    values={pageResponse}
                    onChange={onChange}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Accordion 2: Branch */}
            <Accordion
              expanded={expandedAccordion.panel2}
              onChange={handleAccordionChange('panel2')}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': {
                  display: 'none',
                },
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  },
                }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Branchs
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({branches?.length || 0} available)
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Branch Information, Contact Information, City Potential
                  </Typography>
                </Box>
                <Tooltip title="Add Branch">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleAddBranch();
                    }}
                    sx={{ mr: 1 }}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Import Branches">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setOpenImportDrawer(true);
                    }}
                    sx={{ mr: 1 }}>
                    <FileUploadRoundedIcon />
                  </IconButton>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {companyBranchesTable()}
                  {branches?.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      No branches available
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Accordion 3: Corporate Rates */}
            <Accordion
              expanded={expandedAccordion.panel3}
              onChange={handleAccordionChange('panel3')}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': {
                  display: 'none',
                },
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  },
                }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Corporate Rates
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({corporateRates?.length || 0} available)
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Corporate Rate Information, Room Type vs Corporate Rate, Timeslot Rate Mapping, Corporate Rate
                    Inclusion
                  </Typography>
                </Box>
                <Tooltip title="Add Corporate Rate">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleAddCorporateRate();
                    }}
                    sx={{ mr: 1 }}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <CorporateRateInfo
                    companyId={id}
                    branchId={branchId}
                    assigneeList={assigneeList}
                    setMessage={setMessage}
                    openDrawer={openCorporateRatesDrawer}
                    setOpenDrawer={setOpenCorporateRatesDrawer}
                    corporateRates={corporateRates}
                    setCorporateRates={setCorporateRates}
                    hideAddButton={true}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {isSalesManagementEnabled && (
              <Accordion
                expanded={expandedAccordion.panel4}
                onChange={handleAccordionChange('panel4')}
                sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight={600}>
                    {strings.company_master_company_ledgers}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CompanyLedgerInfo companyId={Number(id)} level="company" />
                </AccordionDetails>
              </Accordion>
            )}

            {/* BranchInfo drawer - only renders drawer, no visible UI */}
            <BranchInfo
              companyId={Number(id) ?? null}
              companyName={pageResponse?.companyName}
              setIsExpanded={() => {}}
              isExpanded={false}
              branchId={branchId}
              setBranches={setBranches}
              setBranchId={setBranchId}
              branches={branches}
              assigneeList={assigneeList}
              countries={countryList}
              setMessage={setMessage}
              openDrawer={openBranchInfoDrawer}
              setOpenDrawer={setOpenBranchInfoDrawer}
              refreshBranches={getInitialResponse}
              hideUI={true}
            />
            <DrawerMenu
              width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
              open={openImportDrawer}
              handleClose={() => setOpenImportDrawer(false)}
              closeButton={true}
              title={`${strings.usermgmt_button_import} ${pageConfig?.title}`}>
              <ListPageImportDrawerComponent
                setOpenDrawer={setOpenImportDrawer}
                isLoading={importLoading}
                setIsLoading={setImportLoading}
                setMessage={setMessage}
                downloadTempateApi={URLS.downloadCompanyBranchesTemplate}
                importApi={URLS.importCompanyBranches(id as number)}
              />
            </DrawerMenu>
            {showDeleteModal && deleteModal}
          </>
        )}
      </AddEdit>
    </>
  );
};
export default AddEditCompanyMaster;
